/* eslint-disable class-methods-use-this */

import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { EMPTY_ARRAY, noop } from "@regrello/core-utils";
import { i18nParseToNumberOrNull } from "@regrello/core-utils/src/utils/numberFormatUtils";
import type {
  FieldFields,
  FieldInstanceFields,
  PropertyTypeFields,
  SpectrumFieldConstraintFields,
  SpectrumFieldValidationTypeFields,
  SpectrumFieldVersionFields,
  SpectrumValueConstraintFields,
  UpdateFieldInstanceValueInputs,
} from "@regrello/graphql-api";
import type { ColDef, ValueFormatterParams, ValueGetterParams, ValueParserParams } from "ag-grid-community";
import type { ReactNode } from "react";
import type { FieldArrayWithId, FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import type { ConfigureSpectrumFieldFormFormFields } from "../../../../../../ui2/fields/configure/_internal/ConfigureSpectrumFieldForm";
import {
  getIsFieldInstanceColumn,
  getIsFormFieldColumn,
  type RowDatum,
} from "../../../../../../ui2/forms/instance/utils/tableV2Utils";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  ValidateBulkUploadCellValueOptions,
} from "../../../customFields/plugins/types/CustomFieldPlugin";
import {
  type ValidationResult,
  validateRequired,
} from "../../../customFields/plugins/utils/validateBulkUploadCellValueUtils";
import { RegrelloFormFieldNumber } from "../../../formFields/RegrelloFormFieldNumber";
import type { ProspectiveSpectrumFieldVersion } from "../../types/ProspectiveSpectrumFieldVersion";
import { SpectrumFieldPluginDecorator } from "../../types/SpectrumFieldPluginDecorator";
import { SpectrumFieldValidationType } from "../../types/SpectrumFieldValidationType";
import {
  FrontendValueConstraintRuleName,
  getDecimalPlacesFromFieldVersion,
  getDisallowGroupingsFloatFromFieldVersion,
  validateByConstraints,
} from "../../utils/spectrumFieldConstraintUtils";
import { RegrelloEditableCellCurrency } from "./_internal/RegrelloEditableCellCurrency";
import { SpectrumCurrencyValueConstraints } from "./_internal/SpectrumCurrencyValueConstraints";
import type { CurrencyFieldPluginFrontendValue } from "./_internal/types";

type VALUE = CurrencyFieldPluginFrontendValue; // Saves some characters without using a troublesome generic type.

export class SpectrumCurrencyFieldPluginDecorator extends SpectrumFieldPluginDecorator<VALUE> {
  constructor(plugin: CustomFieldPlugin<VALUE>) {
    super(plugin);
    this.uri = "com.regrello.spectrumField.currency";
  }

  public canProcessValidationType(spectrumValidationType: ProspectiveSpectrumFieldVersion["validationType"]) {
    return spectrumValidationType.validationType === SpectrumFieldValidationType.CURRENCY;
  }

  public canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion) {
    return (
      this.canProcessPropertyDataType(field.propertyType.dataType) &&
      this.canProcessValidationType(field.validationType)
    );
  }

  public findPropertyTypeFromLoadedPropertyTypes(propertyTypes: PropertyTypeFields[]) {
    return propertyTypes.find((propertyType) => propertyType.dataType === "FLOAT");
  }

  public findValidationTypeFromLoadedValidationTypes(validationTypes: SpectrumFieldValidationTypeFields[]) {
    return validationTypes.find(
      (validationType) => validationType.validationType === SpectrumFieldValidationType.CURRENCY,
    );
  }

  public findValueConstraintsFromLoadedValueConstraints(valueConstraints: SpectrumValueConstraintFields[]) {
    return valueConstraints
      .filter(
        ({ valueConstraintRule }) =>
          valueConstraintRule === FrontendValueConstraintRuleName.MAX_FLOAT ||
          valueConstraintRule === FrontendValueConstraintRuleName.MIN_FLOAT ||
          valueConstraintRule === FrontendValueConstraintRuleName.DECIMAL_PLACES ||
          valueConstraintRule === FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT,
      )
      .sort((a, b) => {
        // Sort order: DECIMAL_PLACES, MIN_INT, MAX_INT
        const order: Partial<Record<FrontendValueConstraintRuleName, number>> = {
          [FrontendValueConstraintRuleName.DECIMAL_PLACES]: 1,
          [FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT]: 2,
          [FrontendValueConstraintRuleName.MIN_FLOAT]: 3,
          [FrontendValueConstraintRuleName.MAX_FLOAT]: 4,
        };

        const aOrder = order[a.valueConstraintRule as FrontendValueConstraintRuleName] ?? 5;
        const bOrder = order[b.valueConstraintRule as FrontendValueConstraintRuleName] ?? 5;

        return aOrder - bOrder;
      });
  }

  public getConstraintConditionOperators() {
    return {
      IS_ZERO: {
        label: t`Is empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      IS_NOT_ZERO: {
        label: t`Is not empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      GT: {
        label: t`Is greater than`,
        inputCount: 1,
        isMultiselect: false,
      },
      GTE: {
        label: t`Is greater than or equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      LT: {
        label: t`Is less than`,
        inputCount: 1,
        isMultiselect: false,
      },
      LTE: {
        label: t`Is less than or equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      EQ: {
        label: t`Is equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      NOT: {
        label: t`Is not equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
    };
  }

  public getSpectrumFormAutosaveMode() {
    return "onBlur" as const;
  }

  public getConstraintsToDisplay(field: SpectrumFieldVersionFields): SpectrumFieldConstraintFields[] {
    const fieldConstraints = field.fieldConstraints;

    const groupingConstraint = fieldConstraints.find(
      (constraint) =>
        constraint.spectrumValueConstraint.valueConstraintRule ===
        FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT,
    );
    if (groupingConstraint != null) {
      return fieldConstraints;
    }

    // (max): This field is missing an explicit grouping field constraint, so we'll create a mock one here to render correctly.
    return [
      ...fieldConstraints,
      {
        id: 0,
        uuid: "",
        createdAt: new Date().toISOString(),
        // eslint-disable-next-line lingui/no-unlocalized-strings
        constraintArgs: ["false"],
        spectrumValueConstraint: {
          uuid: "",
          id: 0,
          createdAt: new Date().toISOString(),
          valueConstraintRule: FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT,
          spectrumFieldPropertyType: field.propertyType,
        },
      },
    ];
  }

  public override renderSpectrumFormField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode => {
    return this.renderFormField(props.fieldInstance?.spectrumFieldVersion.field ?? field, props);
  };

  public renderPreviewFormField(spectrumField: SpectrumFieldVersionFields) {
    return (
      <RegrelloFormFieldNumber
        currencySymbol={spectrumField.fieldUnit?.symbol || "$"}
        disabled={true}
        onChange={noop}
        size="medium"
        value={Number.NaN}
      />
    );
  }

  public renderValueConstraints(props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
  }): ReactNode {
    return <SpectrumCurrencyValueConstraints {...props} />;
  }

  public override parseTableValue(_fieldVersion: SpectrumFieldVersionFields, valueIn: unknown) {
    const valueInCleaned = typeof valueIn === "string" || typeof valueIn === "number" ? valueIn : null;
    return i18nParseToNumberOrNull(valueInCleaned, i18n.locale);
  }

  public getColumnDef<TData extends RowDatum>(
    colDefParams: Partial<ColDef<TData, VALUE>>,
    options: {
      fieldInstance: FieldInstanceFields;
      placeholderRenderer?: (params: ValueFormatterParams<TData, VALUE>) => string;
    },
  ): ColDef<TData, VALUE> {
    const spectrumFieldVersion = options.fieldInstance.spectrumFieldVersion;
    if (spectrumFieldVersion == null) {
      throw new Error("SpectrumFieldVersion is null on fieldInstance, this should never happen");
    }

    const decimalPlaces = getDecimalPlacesFromFieldVersion(spectrumFieldVersion);
    const disallowGroupings = getDisallowGroupingsFloatFromFieldVersion(spectrumFieldVersion);
    const formatOptions = {
      minimumFractionDigits: decimalPlaces ?? undefined,
      maximumFractionDigits: decimalPlaces ?? undefined,
      useGrouping: disallowGroupings === undefined ? undefined : !disallowGroupings,
    } satisfies Intl.NumberFormatOptions;

    return {
      ...colDefParams,
      type: "numericColumn",
      cellClassRules: {
        ...colDefParams.cellClassRules,
        "regrello-center-aligned-cell": (_) => true,
        "rg-grid-not-editable": (params) => {
          // (max): gray cell out if it is not editable.
          if (params.colDef.editable == null) {
            return true;
          }
          if (typeof params.colDef.editable === "boolean") {
            return !params.colDef.editable;
          }
          return !params.colDef.editable(params);
        },
      },
      cellEditor: RegrelloEditableCellCurrency,
      cellEditorParams: {
        fieldInstance: options.fieldInstance,
        formatOptions,
      },
      cellEditorPopup: true,
      cellEditorPopupPosition: "over", // eslint-disable-line lingui/no-unlocalized-strings
      cellRenderer: (params: ColDef["cellRendererParams"]) => {
        const isValueEmpty = params.value == null || params.value === "";
        if (isValueEmpty && options.placeholderRenderer != null) {
          return options.placeholderRenderer(params);
        }
        return (
          <div className="h-full flex items-center gap-1">
            <span>{spectrumFieldVersion.fieldUnit?.symbol}</span>
            {!isValueEmpty && <span>{i18n.number(params.value, formatOptions)}</span>}
          </div>
        );
      },
      headerClass: "regrello-left-aligned-header",
      valueGetter: (params: ValueGetterParams<TData, VALUE>) => {
        const value =
          params.data != null &&
          params.colDef.colId != null &&
          (getIsFormFieldColumn(params.colDef.colId) || getIsFieldInstanceColumn(params.colDef.colId))
            ? params.data[params.colDef.colId]
            : null;

        return this.parseTableValue(spectrumFieldVersion, value);
      },
      valueParser: (params: ValueParserParams<TData, VALUE>) => {
        return this.parseTableValue(spectrumFieldVersion, params.newValue);
      },
    };
  }

  public getFieldUpdateInputsFromFrontendValue(
    fieldInstance: FieldInstanceFields,
    frontendValue: CurrencyFieldPluginFrontendValue,
  ): UpdateFieldInstanceValueInputs {
    const value = i18nParseToNumberOrNull(frontendValue, i18n.locale) ?? undefined;
    return {
      id: fieldInstance.values[0].id,
      inputType: fieldInstance.values[0].inputType,
      floatValue: value,
    };
  }

  public isValidTableColumn(_: SpectrumFieldVersionFields): boolean {
    return true;
  }

  public validateBulkUploadCellValue(
    _field: FieldFields,
    value: string,
    options: ValidateBulkUploadCellValueOptions,
  ): ValidationResult {
    const valueAsString = value.trim();

    const result = validateRequired(valueAsString, options.isRequired);
    if (result != null) {
      return result;
    }

    const valueAsDecimalNumber = Number.parseFloat(valueAsString);

    const spectrumValidationResult = this.validateValueInternal(options.fieldVersion, valueAsDecimalNumber);
    if (!spectrumValidationResult.isValid) {
      return {
        value: valueAsString,
        info: spectrumValidationResult.errors.map((errorString) => ({
          message: errorString,
          level: "error" as const,
        })),
      };
    }

    return {
      value: String(valueAsDecimalNumber),
    };
  }

  public validateValue(
    fieldVersion: SpectrumFieldVersionFields,
    value: unknown,
    isRequired: boolean,
  ): { isValid: boolean; errors: string[] } {
    if (value == null) {
      if (isRequired) {
        return { isValid: false, errors: [t`This field is required`] };
      }
      return { isValid: true, errors: EMPTY_ARRAY };
    }

    if (typeof value !== "number") {
      return {
        isValid: false,
        errors: [t`Must be a number`],
      };
    }

    return this.validateValueInternal(fieldVersion, value);
  }

  private validateValueInternal(
    fieldVersion: SpectrumFieldVersionFields,
    value: VALUE,
  ): { isValid: boolean; errors: string[] } {
    if (value == null) {
      return {
        isValid: true,
        errors: EMPTY_ARRAY,
      };
    }

    if (Number.isNaN(value)) {
      return {
        isValid: false,
        errors: [t`Must be a valid decimal number`],
      };
    }
    if (value < Number.MIN_SAFE_INTEGER || value > Number.MAX_SAFE_INTEGER) {
      return { isValid: false, errors: [t`Number is out of range`] };
    }

    const isLegacyIntType = fieldVersion.propertyType.dataType === "INT";

    const filteredConstraints = isLegacyIntType
      ? fieldVersion.fieldConstraints.filter(
          (constraint) =>
            // Filter out float-valued constraints.
            constraint.spectrumValueConstraint.valueConstraintRule !== FrontendValueConstraintRuleName.MIN_FLOAT &&
            constraint.spectrumValueConstraint.valueConstraintRule !== FrontendValueConstraintRuleName.MAX_FLOAT,
        )
      : fieldVersion.fieldConstraints.filter(
          (constraint) =>
            // Filter out int-valued constraints.
            constraint.spectrumValueConstraint.valueConstraintRule !== FrontendValueConstraintRuleName.MIN_INT &&
            constraint.spectrumValueConstraint.valueConstraintRule !== FrontendValueConstraintRuleName.MAX_INT,
        );

    return validateByConstraints(filteredConstraints, value);
  }
}
